import { applySnapshot, flow, SnapshotIn, types } from 'mobx-state-tree';
import { Church } from '../types';
import { composeError } from '../utils/transforms';
import { withRootStore } from './withRootStore';
import {
  createChurch,
  deleteChurch as deleteChurchApi,
  getChurchById,
  getChurches,
  toggleChurchActive,
  updateChurch,
} from '../services/churchApi';

export const ChurchModel = types.model('ChurchModel', {
  id: types.maybe(types.string),
  name: types.string,
  phoneNumber: types.maybeNull(types.string),
  responsiblePersonName: types.maybeNull(types.string),
  streetAddress: types.maybeNull(types.string),
  postalCode: types.maybeNull(types.string),
  city: types.maybeNull(types.string),
  regionOrLocation: types.maybeNull(types.string),
  sizeInfo: types.maybeNull(types.string),
  isActive: types.boolean,
  updatedAt: types.maybe(types.string),
});

export const ChurchStore = types
  .model('ChurchStore', {
    churches: types.optional(types.array(ChurchModel), []),
    selectedChurch: types.maybe(ChurchModel),
    loading: types.optional(types.boolean, false),
    error: types.frozen(),
  })
  .extend(withRootStore)
  .views((self) => ({
    get itemsArray() {
      return self.churches.slice();
    },
  }))
  .actions((self) => {
    type ChurchSnapshotIn = SnapshotIn<typeof ChurchModel>;

    const mapToModel = (c: Church): ChurchSnapshotIn => ({
      id: c.id != null ? String(c.id) : undefined,
      name: c.name,
      phoneNumber: c.phoneNumber ?? null,
      responsiblePersonName: c.responsiblePersonName ?? null,
      streetAddress: c.streetAddress ?? null,
      postalCode: c.postalCode ?? null,
      city: c.city ?? null,
      regionOrLocation: c.regionOrLocation ?? null,
      sizeInfo: c.sizeInfo ?? null,
      isActive: !!c.isActive,
      updatedAt: c.updatedAt,
    });
    const fetchChurches = flow(function* (search?: string, isActive?: boolean) {
      self.loading = true;
      try {
        const data: Church[] = yield getChurches({ search, isActive });
        const mapped = data.map(mapToModel);
        applySnapshot(self.churches, mapped);
        return self.churches.slice();
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
        return self.churches.slice();
      } finally {
        self.loading = false;
      }
    });

    const fetchChurch = flow(function* (id: string) {
      self.loading = true;
      try {
        const c: Church = yield getChurchById(id);
        self.selectedChurch = ChurchModel.create(mapToModel(c));
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      } finally {
        self.loading = false;
      }
    });

    const saveChurch = flow(function* (payload: Church) {
      try {
        if (payload.id) {
          const updated: Church = yield updateChurch(payload.id, payload);
          const idx = self.churches.findIndex((c) => c.id === payload.id);
          if (idx >= 0) {
            applySnapshot(self.churches[idx], mapToModel(updated));
          } else {
            self.churches.unshift(ChurchModel.create(mapToModel(updated)));
          }
        } else {
          const created: Church = yield createChurch(payload);
          self.churches.unshift(ChurchModel.create(mapToModel(created)));
        }
        self.rootStore.errorStore.setSuccess('save_success');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const setChurchActive = flow(function* (id: string, isActive: boolean) {
      try {
        yield toggleChurchActive(id, isActive);
        const idx = self.churches.findIndex((c) => c.id === id);
        if (idx >= 0) self.churches[idx].isActive = isActive;
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    const clearSelected = () => {
      self.selectedChurch = undefined;
    };

    const deleteChurch = flow(function* (id: string) {
      try {
        yield deleteChurchApi(id);
        const idx = self.churches.findIndex((c) => c.id === id);
        if (idx >= 0) self.churches.splice(idx, 1);
        if (self.selectedChurch?.id === id) self.selectedChurch = undefined;
        self.rootStore.errorStore.setSuccess('delete_success');
      } catch (err) {
        const error = composeError(err);
        self.rootStore.errorStore.setError(error.message);
        self.error = error;
      }
    });

    return { fetchChurches, fetchChurch, saveChurch, setChurchActive, deleteChurch, clearSelected };
  });

export type ChurchStoreInstance = typeof ChurchStore.Type;
